import { useRef, useState } from "react";
import { Camera, Droplets, ImageIcon, Loader2 } from "lucide-react";
import { waterLimit } from "@/lib/safety";
import { Card } from "@/components/kit";
import { fileToJpegBase64, mapProduct, planFromReading, readingTitle, scanVeeder } from "@/lib/veeder";
import { formatGal, productColor } from "@/lib/tank";
import { useT } from "@/lib/i18n";

function ReadingChoice({ r, i, onPick }) {
  const t = useT();
  const parts = [readingTitle(r, t), r.inches != null ? `${r.inches.toFixed(2)}"` : null,
    r.gallons != null ? `${formatGal(r.gallons)} gal` : t("no volume")].filter(Boolean);
  return (
    <button type="button" className="scan-choice" data-testid={`scan-choice-${i}`} onClick={() => onPick(r)}>
      <span className="grade-dot grade-dot-lg" style={{ background: productColor(mapProduct(r.product)) }} />{parts.join(" · ")}{r.ullageGallons != null ? ` · ${t("ullage")} ${formatGal(r.ullageGallons)}` : ""}
    </button>
  );
}

export function VeederScan({ compact, onApply }) {
  const t = useT();
  const camRef = useRef(null), libRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState(false);
  const [msg, setMsg] = useState(null);
  const [choices, setChoices] = useState([]);

  const applyReading = (r) => {
    setChoices([]);
    const plan = planFromReading(r, t);
    setMsg({ tone: plan.tone, text: plan.note, why: plan.why, check: plan.check, water: plan.water });
    onApply(plan);
  };

  const run = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true); setMsg(null); setChoices([]);
    let b64;
    try { b64 = await fileToJpegBase64(file); } catch {
      setBusy(false); setMsg({ tone: "danger", text: t("Couldn't open that photo. Take it again.") }); return;
    }
    const res = await scanVeeder(b64);
    setBusy(false);
    if (!res.ok) return setMsg({ tone: "danger", text: t(res.error) });
    if (res.readings.length > 1) {
      setChoices(res.readings);
      return setMsg({ tone: "caution", text: t("That screen has more than one product. Tap the one you want.") });
    }
    applyReading(res.readings[0]);
  };

  return (
    <Card testID="veeder-scan" className={compact ? "scan-compact" : ""}>
      {!compact && (
        <>
          <div className="section-title">{t("Scan the console")}</div>
          <p className="sub scan-sub">{t("Photo of the Veeder-Root. If several products are on it, you pick the one you want.")}</p>
        </>
      )}
      <div className="scan-row scan-pick">
        <button type="button" className="primary-btn scan-btn" data-testid="scan-console-button" disabled={busy} aria-expanded={pick} onClick={() => setPick((v) => !v)}>
          {busy ? <Loader2 size={16} className="spin" /> : <Camera size={16} />}{busy ? t("Reading") : t("Scan console")}
        </button>
        {pick && !busy && (
          <div className="scan-menu" data-testid="scan-console-menu" role="menu">
            <button type="button" role="menuitem" className="ghost-btn" data-testid="scan-camera-option" onClick={() => { setPick(false); camRef.current?.click(); }}><Camera size={16} />{t("Take a photo")}</button>
            <button type="button" role="menuitem" className="ghost-btn" data-testid="scan-photo-button" onClick={() => { setPick(false); libRef.current?.click(); }}><ImageIcon size={16} />{t("Choose from photos")}</button>
          </div>
        )}
      </div>
      <input ref={camRef} type="file" accept="image/*" capture="environment" hidden data-testid="scan-camera-input" onChange={run} />
      <input ref={libRef} type="file" accept="image/jpeg,image/png,image/webp" hidden data-testid="scan-file-input" onChange={run} />
      <p className="scan-caution" data-testid="scan-caution">{t("AI readings can be off — always confirm against the console before you drop.")}</p>
      {choices.length > 0 && <div className="scan-choices">{choices.map((r, i) => <ReadingChoice key={i} r={r} i={i} onPick={applyReading} />)}</div>}
      {msg && <div className={`scan-message tone-${msg.tone}`} data-testid="scan-message">{msg.text}</div>}
      {msg?.water > 0 && (
        <div className={`drop-warn drop-warn-${msg.water >= waterLimit() ? "danger" : "caution"}`} data-testid="scan-water-warning" data-level={msg.water >= waterLimit() ? "high" : "some"} role="alert">
          <Droplets size={22} /><b className="drop-warn-head">{t("Water is showing ({w}\"). Confirm the fuel height before you drop.", { w: msg.water.toFixed(2) })}</b>
        </div>
      )}
      {msg?.check && <div className={`scan-message tone-${msg.check.ok ? "safe" : "danger"}`} data-testid="scan-after-check">{msg.check.text}</div>}
      {msg?.why && <p className="scan-why" data-testid="scan-why"><b>{t("Why this size")}:</b> {msg.why}</p>}
    </Card>
  );
}
